import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useRecoilValue } from 'recoil'
import { pageZoomAtom } from 'atoms'
import { getTextContentBounds } from '@/utils/editor/getTextContentBounds'

type UseTextContentBoundsParams = {
  TinyMCERef: React.RefObject<{
    bodyElement?: HTMLElement | null
  } | null>
  containerWidth: number
  containerHeight: number
  itemState: string
}

type AdjustedBounds = {
  adjustedX: number
  adjustedY: number
  adjustedWidth: number
  adjustedHeight: number
}

/**
 * Hook that calculates adjusted item bounds based on actual text content.
 * Uses MutationObserver to track changes to text content and styling.
 *
 * IMPORTANT: Due to font rendering differences at various zoom levels, we calculate
 * bounds as RATIOS of the container size, not absolute pixel values. This ensures
 * consistent positioning regardless of zoom level.
 *
 * @param TinyMCERef - Reference to TinyMCE editor instance
 * @param containerWidth - Width of the text box container
 * @param containerHeight - Height of the text box container
 * @param itemState - Current state of the item ('loading', 'idle', etc.)
 * @returns Adjusted bounds for bleed detection
 */
export const useTextContentBounds = ({
  TinyMCERef,
  containerWidth,
  containerHeight,
  itemState,
}: UseTextContentBoundsParams): AdjustedBounds => {
  const zoom = useRecoilValue(pageZoomAtom)

  const defaultBounds: AdjustedBounds = {
    adjustedX: 0,
    adjustedY: 0,
    adjustedWidth: containerWidth,
    adjustedHeight: containerHeight,
  }

  const [bounds, setBounds] = useState<AdjustedBounds>(defaultBounds)

  // Store ratios instead of absolute values to be zoom-independent
  // These represent the text position/size as a fraction of the container
  const ratiosRef = useRef<{
    xRatio: number
    yRatio: number
    widthRatio: number
    heightRatio: number
  } | null>(null)

  const calculateBounds = useCallback((): AdjustedBounds => {
    const bodyElement = TinyMCERef?.current?.bodyElement

    if (!bodyElement || itemState === 'loading') {
      return {
        adjustedX: 0,
        adjustedY: 0,
        adjustedWidth: containerWidth,
        adjustedHeight: containerHeight,
      }
    }

    const textBounds = getTextContentBounds(bodyElement)

    if (!textBounds) {
      return {
        adjustedX: 0,
        adjustedY: 0,
        adjustedWidth: containerWidth,
        adjustedHeight: containerHeight,
      }
    }

    // Get the container's current screen size (affected by zoom)
    const containerRect = bodyElement.getBoundingClientRect()
    const screenContainerWidth = containerRect.width
    const screenContainerHeight = containerRect.height

    // Calculate ratios: where is the text positioned within the container?
    // These ratios should be stable regardless of zoom level
    const xRatio =
      screenContainerWidth > 0
        ? textBounds.offsetLeft / screenContainerWidth
        : 0
    const yRatio =
      screenContainerHeight > 0
        ? textBounds.offsetTop / screenContainerHeight
        : 0
    const widthRatio =
      screenContainerWidth > 0
        ? textBounds.contentWidth / screenContainerWidth
        : 1
    const heightRatio =
      screenContainerHeight > 0
        ? textBounds.contentHeight / screenContainerHeight
        : 1

    // Store the ratios for debugging
    ratiosRef.current = { xRatio, yRatio, widthRatio, heightRatio }

    // Convert ratios back to page coordinates using container dimensions
    const rawAdjustedX = xRatio * containerWidth
    const rawAdjustedY = yRatio * containerHeight
    const rawAdjustedWidth = widthRatio * containerWidth
    const rawAdjustedHeight = heightRatio * containerHeight

    // Clamp all bounds to container dimensions
    const clampedWidth = Math.min(Math.max(0, rawAdjustedWidth), containerWidth)
    const clampedHeight = Math.min(
      Math.max(0, rawAdjustedHeight),
      containerHeight,
    )

    // Clamp position so content stays within container
    const adjustedX = Math.min(
      Math.max(0, rawAdjustedX),
      containerWidth - clampedWidth,
    )
    const adjustedY = Math.min(
      Math.max(0, rawAdjustedY),
      containerHeight - clampedHeight,
    )
    const adjustedWidth = clampedWidth
    const adjustedHeight = clampedHeight

    return {
      adjustedX,
      adjustedY,
      adjustedWidth,
      adjustedHeight,
    }
  }, [TinyMCERef, containerWidth, containerHeight, itemState])

  // Use requestAnimationFrame to ensure we measure after browser layout
  const rafRef = useMemo(() => ({ current: 0 }), [])

  const scheduleUpdate = useCallback(() => {
    // Cancel any pending RAF
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
    }
    // Schedule update for next frame (after layout)
    rafRef.current = requestAnimationFrame(() => {
      setBounds(calculateBounds())
    })
  }, [calculateBounds, rafRef])

  useEffect(() => {
    // Initial calculation after a frame to ensure DOM is ready
    rafRef.current = requestAnimationFrame(() => {
      setBounds(calculateBounds())
    })

    const bodyElement = TinyMCERef?.current?.bodyElement
    if (!bodyElement) return

    // Watch for DOM changes (text content, styling, etc.)
    const observer = new MutationObserver(() => {
      scheduleUpdate()
    })

    observer.observe(bodyElement, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    })

    return () => {
      observer.disconnect()
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [TinyMCERef?.current?.bodyElement, calculateBounds, scheduleUpdate])

  // Recalculate when container size or zoom changes
  useEffect(() => {
    scheduleUpdate()
  }, [containerWidth, containerHeight, zoom, scheduleUpdate])

  return bounds
}
